// Native replacement for dashboard/src/components/LoginPage.ts — proves the
// mini-app registry's auth entry point doesn't need dashboard/ either, the
// same way Gigs proved the identity spectrum and Concierge proved the
// registry contract. Rendered both server-side (authServer.ts) and
// client-side (authClient.ts); the form itself needs no per-visitor data, so
// SSR renders the real page, not a skeleton — no window/localStorage read
// happens until handleSubmit, which only ever runs client-side.
import { h } from 'preact';
import { useState } from 'preact/hooks';
import { setUser, type User } from '../clientSession.js';

interface State {
  email: string;
  password: string;
  error: string;
  loading: boolean;
}

// Where to land after a successful login — honors ?next=, set by whoever
// sent the visitor here (Shell.ts's sign-in link, most often) so signing in
// returns them to where they were instead of always landing on the mini-app
// home. Restricted to a same-origin relative path (must start with '/', must
// not start with '//' — a protocol-relative URL is an open redirect to
// another host) so a crafted ?next= can never send a signed-in session
// off-site. Ported verbatim from dashboard/src/components/LoginPage.ts.
function safeNextPath(): string | null {
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next || !next.startsWith('/') || next.startsWith('//')) return null;
  return next;
}

export function LoginPage() {
  const [state, setState] = useState<State>({ email: '', password: '', error: '', loading: false });

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setState(s => ({ ...s, loading: true, error: '' }));
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: state.email, password: state.password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error === 'invalid_credentials' ? 'Invalid credentials' : (body?.error || 'login_failed'));
      setUser(body.user as User);
      window.location.href = safeNextPath() ?? '/app';
    } catch (err) {
      setState(s => ({ ...s, loading: false, error: (err as Error).message }));
    }
  }

  const { email, password, error, loading } = state;
  return h('div', { class: 'auth-screen' },
    h('div', { class: 'auth-card' },
      h('h1', { class: 'auth-title' }, 'Oak City Intelligence'),
      error ? h('p', { class: 'auth-error' }, error) : null,
      h('form', { onSubmit: handleSubmit },
        h('div', { class: 'auth-field' },
          h('label', null, 'Email'),
          h('input', {
            type: 'email', value: email, required: true,
            onInput: (e: Event) => setState(s => ({ ...s, email: (e.target as HTMLInputElement).value })),
          }),
        ),
        h('div', { class: 'auth-field' },
          h('label', null, 'Password'),
          h('input', {
            type: 'password', value: password, required: true,
            onInput: (e: Event) => setState(s => ({ ...s, password: (e.target as HTMLInputElement).value })),
          }),
        ),
        h('button', { type: 'submit', class: 'auth-submit', disabled: loading }, loading ? 'Signing in…' : 'Sign in'),
      ),
      h('p', { class: 'auth-alt' }, h('a', { href: '/return' }, 'No password? Email me a sign-in link')),
    ),
  );
}
